/**
 * Coming Soon — the receipt's behaviour.
 *
 * Everything here is an enhancement over markup that already works. Without
 * this file the page still collects an email and a mobile, still posts to
 * Shopify, and still shows the staff password form — it just does so as a
 * plain stack of fields.
 *
 * The one hard rule: never intercept the signup submit. This store has
 * Shopify's invisible CAPTCHA switched on for customer forms, and the token is
 * attached to a real form POST. A fetch() of the same data is answered with
 * "Missing CAPTCHA token" and no visible error, so the submit handler below
 * only rewrites a field value and then gets out of the way.
 */

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_MIN_DIGITS = 9;
const HOLD_MS = 10000;
const MORPH_MS = 240;

const digits = (value) => value.replace(/\D/g, '').length;
const validEmail = (value) => EMAIL.test(value.trim());
const validPhone = (value) => digits(value) >= PHONE_MIN_DIGITS;
const pad = (n) => String(n).padStart(2, '0');

class OdelriPassword extends HTMLElement {
  connectedCallback() {
    this.root = this.querySelector('.coming-soon') ?? this;
    this.base = Number(this.root.dataset.baseReward) || 0;
    this.bonus = Number(this.root.dataset.bonusReward) || 0;

    this.email = this.querySelector('#ComingSoonEmail');
    this.phone = this.querySelector('#ComingSoonPhone');
    this.dial = this.querySelector('#ComingSoonDial');

    // Announce the upgrade last, so nothing below can leave the page in a
    // half-enhanced state with the staff card already hidden.
    this.#paintTeeth();
    this.#paintBars();
    this.#paintMeta();
    this.#startClock();
    this.#buildDial();
    this.#wire();
    this.#sync();
    this.#restoreEmail();
    this.root.classList.add('coming-soon--enhanced');
  }

  disconnectedCallback() {
    clearInterval(this.clockTimer);
    clearInterval(this.holdTimer);
    clearTimeout(this.morphTimer);
    document.removeEventListener('pointerdown', this.onDocDown);
  }

  /* ----------------------------------------------------------- painting --- */

  /**
   * The torn edge, as one tiling triangle. Built here rather than in the
   * stylesheet because the fill has to be the paper colour the merchant chose,
   * and a data URI cannot read a custom property.
   */
  #paintTeeth() {
    const paper = getComputedStyle(this.root).getPropertyValue('--color-bg').trim() || '#FBF8F3';
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="10" viewBox="0 0 16 10"><path d="M0 10L8 0l8 10z" fill="${paper}"/></svg>`;
    this.root.style.setProperty('--cs-teeth', `url("data:image/svg+xml,${encodeURIComponent(svg)}")`);
  }

  /**
   * Bar widths are derived from the index, not random, so the barcode is the
   * same on every render and never reshuffles while the clock ticks beside it.
   */
  #paintBars() {
    const host = this.querySelector('.receipt__bars');
    if (!host) return;

    const widths = [1, 2, 3, 1, 2];
    host.replaceChildren(
      ...Array.from({ length: 46 }, (_, i) => {
        const bar = document.createElement('span');
        bar.style.inlineSize = `${widths[i % 5] + (i % 7 === 0 ? 2 : 0)}px`;
        bar.style.blockSize = i % 11 === 0 ? '82%' : '100%';
        return bar;
      }),
    );
  }

  #paintMeta() {
    const now = new Date();
    const date = this.querySelector('[data-cs-date]');
    const time = this.querySelector('[data-cs-time]');
    if (date) date.textContent = `${pad(now.getDate())}/${pad(now.getMonth() + 1)}/${now.getFullYear()}`;
    if (time) time.textContent = `${pad(now.getHours())}:${pad(now.getMinutes())}`;
    this.#paintRef();
  }

  /** A stable-looking docket number derived from the email, as the design does. */
  #paintRef() {
    const ref = this.querySelector('[data-cs-ref]');
    if (!ref) return;

    const seed = (this.email?.value || this.storedEmail || 'guest')
      .split('')
      .reduce((sum, ch) => sum + ch.charCodeAt(0), 0);
    ref.textContent = `ODL-${(Math.abs(seed) % 9000) + 1000}`;
  }

  #startClock() {
    const host = this.querySelector('[data-cs-countdown]');
    const clock = this.querySelector('[data-cs-clock]');
    if (!host || !clock) return;

    const launch = new Date(host.dataset.launch).getTime();
    if (Number.isNaN(launch)) {
      host.hidden = true;
      return;
    }

    const tick = () => {
      const left = Math.max(0, launch - Date.now());
      const parts = [
        ['d', Math.floor(left / 86400000)],
        ['h', pad(Math.floor(left / 3600000) % 24)],
        ['m', pad(Math.floor(left / 60000) % 60)],
        ['s', pad(Math.floor(left / 1000) % 60)],
      ];
      clock.replaceChildren(
        ...parts.map(([unit, value]) => {
          const span = document.createElement('span');
          span.append(String(value));
          const small = document.createElement('small');
          small.textContent = unit;
          span.append(small);
          return span;
        }),
      );
    };

    tick();
    this.clockTimer = setInterval(tick, 1000);
  }

  /* --------------------------------------------------------- dial picker --- */

  /**
   * The <select> stays in the DOM and stays authoritative — it is what a
   * keyboard and a screen reader drive, and what posts with no JavaScript. The
   * button and panel are a skin over it, and every path through them writes
   * back to the select rather than holding their own idea of the value.
   */
  #buildDial() {
    if (!this.dial || !this.dial.options.length) return;

    const field = this.dial.closest('.receipt__field');
    if (!field) return;

    const flags = this.querySelector('[data-cs-flags]');
    this.flagFor = (iso) => flags?.content.querySelector(`[data-iso="${iso}"]`)?.cloneNode(true) ?? null;

    this.dialButton = document.createElement('button');
    this.dialButton.type = 'button';
    this.dialButton.className = 'receipt__dial-button';
    this.dialButton.setAttribute('aria-expanded', 'false');
    this.dialButton.setAttribute('aria-label', this.dial.labels?.[0]?.textContent?.trim() ?? 'Country dialling code');

    this.dialPanel = document.createElement('div');
    this.dialPanel.className = 'receipt__dial-panel';
    this.dialPanel.setAttribute('role', 'listbox');
    this.dialPanel.hidden = true;

    for (const option of this.dial.options) {
      const row = document.createElement('button');
      row.type = 'button';
      row.className = 'receipt__dial-option';
      row.setAttribute('role', 'option');
      row.dataset.value = option.value;

      const flag = document.createElement('span');
      flag.className = 'receipt__dial-flag';
      const drawing = this.flagFor(option.dataset.iso);
      if (drawing) flag.append(...drawing.childNodes);

      const name = document.createElement('span');
      name.className = 'receipt__dial-option-name';
      name.textContent = option.textContent.replace(/\s*\([^)]*\)\s*$/, '').trim();

      const code = document.createElement('span');
      code.className = 'receipt__dial-option-dial';
      code.textContent = option.value;

      row.append(flag, name, code);
      row.addEventListener('click', () => {
        this.dial.value = option.value;
        this.dial.dispatchEvent(new Event('change', { bubbles: true }));
        this.#closeDial();
        this.dialButton.focus();
      });
      this.dialPanel.append(row);
    }

    this.dialButton.addEventListener('click', () => {
      this.dialPanel.hidden ? this.#openDial() : this.#closeDial();
    });

    this.onDocDown = (event) => {
      if (!this.dialPanel.hidden && !event.target.closest?.('.receipt__field--phone')) this.#closeDial();
    };
    document.addEventListener('pointerdown', this.onDocDown);

    this.dialPanel.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      this.#closeDial();
      this.dialButton.focus();
    });

    field.prepend(this.dialButton);
    field.append(this.dialPanel);
    this.#paintDialButton();
  }

  #openDial() {
    this.dialPanel.hidden = false;
    this.dialButton.setAttribute('aria-expanded', 'true');
  }

  #closeDial() {
    this.dialPanel.hidden = true;
    this.dialButton.setAttribute('aria-expanded', 'false');
  }

  #paintDialButton() {
    if (!this.dialButton) return;

    const selected = this.dial.selectedOptions[0];
    const flag = document.createElement('span');
    flag.className = 'receipt__dial-flag';
    const drawing = this.flagFor(selected?.dataset.iso);
    if (drawing) flag.append(...drawing.childNodes);

    const caret = document.createElement('span');
    caret.className = 'receipt__dial-caret';

    this.dialButton.replaceChildren(flag, document.createTextNode(this.dial.value), caret);

    for (const row of this.dialPanel.querySelectorAll('.receipt__dial-option')) {
      row.setAttribute('aria-selected', String(row.dataset.value === this.dial.value));
    }
  }

  /* ------------------------------------------------------------- wiring --- */

  #wire() {
    for (const input of [this.email, this.phone]) {
      input?.addEventListener('input', () => this.#sync());
    }
    this.dial?.addEventListener('change', () => {
      this.#paintDialButton();
      this.#sync();
    });

    const form = this.querySelector('#ComingSoonSignup');
    form?.addEventListener('submit', (event) => this.#onSubmit(event));

    const barcode = this.querySelector('[data-cs-barcode]');
    if (barcode) {
      barcode.addEventListener('pointerdown', () => this.#holdStart());
      for (const done of ['pointerup', 'pointerleave', 'pointercancel']) {
        barcode.addEventListener(done, () => this.#holdEnd());
      }
    }

    this.querySelector('[data-cs-exit]')?.addEventListener('click', () => this.#morphTo('offer'));
    this.querySelector('[data-cs-upgrade]')?.addEventListener('click', () => this.#upgrade());
  }

  /** Reflect the two fields into the receipt's arithmetic. */
  #sync() {
    const emailOk = validEmail(this.email?.value ?? '');
    const phoneOk = validPhone(this.phone?.value ?? '');
    // Both sides of the receipt ship in the markup so the claimed one can be
    // revealed without a round trip. Which is showing is the state.
    const claimed = this.querySelector('[data-cs-claimed]')?.hidden === false;

    this.#setField('email', emailOk);
    this.#setField('phone', phoneOk);

    const earnedEmail = claimed || emailOk;
    this.#setValue('email', earnedEmail, this.base);
    this.#setValue('phone', phoneOk, this.bonus);

    const total = (earnedEmail ? this.base : 0) + (phoneOk ? this.bonus : 0);
    const totalEl = this.querySelector('[data-cs-total]');
    if (totalEl) totalEl.textContent = `$${total}.00`;

    const cta = this.querySelector('[data-cs-submit]');
    const label = this.querySelector('[data-cs-cta-label]');
    if (cta && label) {
      // aria-disabled, never the disabled property: a disabled button cannot
      // hold focus or be read, and this one has to explain what it is waiting
      // for. The submit is stopped in the handler instead.
      cta.toggleAttribute('data-ready', emailOk);
      cta.setAttribute('aria-disabled', String(!emailOk));
      const ready = cta.dataset.ctaReady ?? '';
      const idle = cta.dataset.ctaIdle ?? label.textContent;
      label.textContent = emailOk ? ready.replace('[amount]', String(this.base + (phoneOk ? this.bonus : 0))) : idle;
    }

    this.#paintRef();
  }

  #setField(name, ok) {
    const field = this.querySelector(`[data-cs-field="${name}"]`);
    const tick = this.querySelector(`[data-cs-tick="${name}"]`);
    field?.toggleAttribute('data-valid', ok);
    if (tick) tick.hidden = !ok;
  }

  #setValue(name, earned, amount) {
    const value = this.querySelector(`[data-cs-value="${name}"]`);
    const row = this.querySelector(`[data-cs-row="${name}"]`);
    // The amount is printed either way; only its weight and colour move, so
    // there is no second string to swap back in.
    value?.toggleAttribute('data-earned', earned);
    if (row) {
      row.toggleAttribute('data-earned', earned);
      const dd = row.querySelector('dd');
      if (dd) dd.textContent = earned ? `$${amount}.00` : '$0.00';
    }
  }

  /**
   * Validate, fold the dial code into the posted number, and let the browser
   * submit. preventDefault is used only to refuse a submission — never to take
   * over one, because that is what loses the CAPTCHA token.
   */
  #onSubmit(event) {
    const error = this.querySelector('[data-cs-error]');
    const fail = (message) => {
      event.preventDefault();
      if (!error) return;
      error.textContent = message;
      error.hidden = false;
    };

    if (error) {
      error.hidden = true;
      error.textContent = '';
    }

    if (!validEmail(this.email?.value ?? '')) {
      fail(this.root.dataset.errorEmail);
      this.email?.focus();
      return;
    }

    const typed = this.phone?.value.trim() ?? '';
    if (typed && !validPhone(typed)) {
      fail(this.root.dataset.errorPhone);
      this.phone?.focus();
      return;
    }

    // Post one number the merchant can dial. Left alone when the shopper has
    // already typed their own country code, so nobody gets +61+61.
    if (typed && this.dial && !typed.startsWith('+')) {
      this.phone.value = `${this.dial.value} ${typed.replace(/^0+/, '')}`;
    }

    try {
      sessionStorage.setItem('odelri:cs-email', this.email.value.trim());
      // Shopify's reply says a signup landed, never what came with it. Remember
      // the number so a receipt rendered by the server — after a challenge, or
      // on the fallback submit — still credits the mobile bonus instead of
      // showing it pending when it was given.
      sessionStorage.setItem('odelri:cs-phone', this.phone?.value.trim() ?? '');
    } catch {
      /* private mode: the success copy falls back to generic wording */
    }

    // Post without leaving the page. The body is this form's own FormData, so
    // the h-captcha-response Shopify wired up is in it — the CAPTCHA still runs
    // and is still checked, only the response stops replacing the page.
    event.preventDefault();
    this.#send(event.target);
  }

  /**
   * Shopify's documented hook for a form submitted from script. protect()
   * guarantees CAPTCHA is wired before the body is read; with CAPTCHA turned
   * off in the admin the hook is simply absent and the post goes as-is.
   */
  #send(form) {
    const post = () => this.#post(form);
    const protect = window.Shopify?.captcha?.protect;

    if (typeof protect === 'function') protect(form, post);
    else post();
  }

  async #post(form) {
    const cta = this.querySelector('[data-cs-submit]');
    cta?.setAttribute('aria-busy', 'true');

    let response;
    try {
      response = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        redirect: 'follow',
      });
    } catch {
      // The request never landed, so nothing was created. Hand the submission
      // back to the browser rather than swallowing it — a signup that silently
      // does nothing is worse than one that costs a page load.
      form.submit();
      return;
    } finally {
      cta?.removeAttribute('aria-busy');
    }

    // hCaptcha decided this one needs a human. The POST was diverted, so no
    // customer exists yet; submit for real and let them answer it.
    if (/\/challenge\b/.test(response.url ?? '')) {
      form.submit();
      return;
    }

    // Status is not the answer. Shopify replies to a rejected signup with the
    // page re-rendered and form.errors set — 200 either way — so a status check
    // would stamp the receipt on a refusal. The server marks a real success
    // with [data-cs-posted]; that is what gets read.
    const returned = new DOMParser().parseFromString(await response.text(), 'text/html');

    if (returned.querySelector('[data-cs-posted]')) {
      this.#claim();
      return;
    }

    const error = this.querySelector('[data-cs-error]');
    if (error) {
      // Prefer Shopify's own wording — it names the real reason — and fall
      // back to ours when the response carried none.
      const reported = returned.querySelector('.receipt__error:not([hidden])')?.textContent.trim();
      error.textContent = reported || this.root.dataset.errorGeneric;
      error.hidden = false;
    }
  }

  /** Turn the receipt over to its claimed side, in place. */
  #claim() {
    const phoneEarned = validPhone(this.phone?.value ?? '');

    const success = this.querySelector('[data-cs-success]');
    if (success) {
      const address = this.email?.value.trim() ?? '';
      const template = success.dataset.template ?? success.textContent;
      success.textContent = address
        ? template.replace('[email]', address)
        : template.replace(/\s*\[email\]/, '');
    }

    // A hidden required field cannot be focused, and the browser refuses to
    // submit a form containing one. The address is already captured, so the
    // constraint has done its job.
    if (this.email) this.email.required = false;
    this.querySelector('[data-cs-field="email"]')?.toggleAttribute('hidden', true);
    // The phone field glows blush while it is still wanted. Next to a stamped
    // receipt that reads as a refusal rather than an invitation, so it is put
    // away and the upgrade button is what asks for the number.
    this.querySelector('[data-cs-field="phone"]')?.toggleAttribute('hidden', true);
    this.querySelector('[data-cs-actions]')?.toggleAttribute('hidden', true);
    this.querySelector('[data-cs-claimed]')?.toggleAttribute('hidden', false);
    this.querySelector('[data-cs-stamp]')?.toggleAttribute('hidden', false);
    this.querySelector('[data-cs-copy]')?.toggleAttribute('hidden', false);
    this.querySelector('[data-cs-upgrade]')?.toggleAttribute('hidden', phoneEarned);

    const error = this.querySelector('[data-cs-error]');
    if (error) error.hidden = true;

    this.#sync();
  }

  /** "Add mobile" — the same form again, with the number this time. */
  #upgrade() {
    this.querySelector('[data-cs-claimed]')?.toggleAttribute('hidden', true);
    this.querySelector('[data-cs-stamp]')?.toggleAttribute('hidden', true);
    this.querySelector('[data-cs-field="phone"]')?.toggleAttribute('hidden', false);
    this.querySelector('[data-cs-actions]')?.toggleAttribute('hidden', false);
    this.phone?.focus();
  }

  /**
   * Put the address back into the success copy after a real page load — the
   * server-rendered path, and the one a browser with no JavaScript takes. The
   * in-place claim does its own substitution from the field, which is still
   * filled in.
   */
  #restoreEmail() {
    try {
      this.storedEmail = sessionStorage.getItem('odelri:cs-email') || '';
    } catch {
      this.storedEmail = '';
    }

    const success = this.querySelector('[data-cs-success]');
    const claimed = this.querySelector('[data-cs-claimed]');
    if (success && claimed && !claimed.hidden) {
      const template = success.dataset.template ?? success.textContent;
      success.textContent = this.storedEmail
        ? template.replace('[email]', this.storedEmail)
        : template.replace(/\s*\[email\]/, '');

      // Put the remembered number back so the arithmetic on a server-rendered
      // receipt matches what was actually submitted, and so the upgrade button
      // is not offered for a mobile they already gave.
      let storedPhone = '';
      try {
        storedPhone = sessionStorage.getItem('odelri:cs-phone') || '';
      } catch {
        storedPhone = '';
      }
      if (storedPhone && this.phone) this.phone.value = storedPhone;
      this.querySelector('[data-cs-upgrade]')?.toggleAttribute('hidden', validPhone(storedPhone));
      this.#sync();
    }
    this.#paintRef();
  }

  /* --------------------------------------------------- the staff door --- */

  #holdStart() {
    if (this.root.classList.contains('coming-soon--staff')) return;

    const started = Date.now();
    this.root.classList.add('coming-soon--pressing');
    clearInterval(this.holdTimer);
    this.holdTimer = setInterval(() => {
      const progress = (Date.now() - started) / HOLD_MS;
      if (progress >= 1) {
        this.#holdEnd();
        this.#morphTo('staff');
        return;
      }
      this.root.style.setProperty('--cs-press', String(progress));
    }, 100);
  }

  #holdEnd() {
    clearInterval(this.holdTimer);
    this.root.classList.remove('coming-soon--pressing');
    this.root.style.removeProperty('--cs-press');
  }

  /** Fade the paper out, swap its contents, fade back — one move, not two. */
  #morphTo(card) {
    clearTimeout(this.morphTimer);
    this.root.classList.add('coming-soon--morphing');
    this.morphTimer = setTimeout(() => {
      this.root.classList.toggle('coming-soon--staff', card === 'staff');
      this.root.classList.remove('coming-soon--morphing');
      const focus = this.querySelector(
        card === 'staff' ? '#ComingSoonPassword' : '[data-cs-barcode]',
      );
      focus?.focus?.();
    }, MORPH_MS);
  }

}

if (!customElements.get('odelri-password')) {
  customElements.define('odelri-password', OdelriPassword);
}
